/**
 * Every GraphQL document the AI pricing screen sends, built against the frozen
 * contract in `parsina.bff.admin/src/graphql/typeDefs/aiCost.js` field for
 * field. They live in one file so a contract change is a diff against one
 * place, and so a test can match a call by the operation name it carries.
 */

const PRICE_FIELDS = `
  id modelId effectiveFrom
  inputUsdPerM outputUsdPerM cachedInputUsdPerM audioInputUsdPerM audioUsdPerMinute requestUsd
  source note createdAt createdBy
`;

const SERVICE_ROW_FIELDS = `
  serviceKey labelFa labelEn walletServiceType chargeMode chargeCondition
  defaultParseh declaredEstimateUsd active
  invocations chargedRate avgCalls avgInputTokens avgOutputTokens avgCachedTokens
  meanCostUsd p50CostUsd p90CostUsd pricedShare costPerMinuteUsd totalCostUsd
  revenueParseh revenueUsd marginPct meanCostToman
  effectiveParseh priceSource unit declaredInAppsParseh appsMismatch
  models { modelId role calls avgInputTokens avgOutputTokens costUsd shareOfCostPct shareOfCallsPct unpriced }
  hasUnpricedModel declaredRolesMismatch
`;

const DAILY_FIELDS = 'day costUsd revenueUsd invocations calls';

const INVOCATION_FIELDS = `
  requestId serviceKey ownerType ownerId appointmentId startedAt finishedAt status
  calls inputTokens outputTokens cachedTokens audioSeconds costUsd providerCostUsd
  chargedParseh chargeReason priceSource walletTransactionId billedMinutes
`;

const POLICY_FIELDS = `
  id status name fxTomanPerUsd targetMarginPct overheadPct costBasis windowDays rounding
  minParseh note parsehToman version computedAt activatedAt activatedBy archivedAt
  createdAt createdBy servicesPriced negativeMargins creditPrice creditCurrency
`;

const POLICY_DETAIL_FIELDS = `
  policy { ${POLICY_FIELDS} }
  prices {
    serviceKey labelFa labelEn unit basisCostUsd basisSource basisSample chargedRate
    costParseh computedParseh overrideParseh effectiveParseh marginPct activeParseh
    deltaParseh defaultParseh
  }
  walletParsehToman projectedBlendedMarginPct cheaper dearer negative
`;

// ── overview ──────────────────────────────────────────────────────────────────

export const OVERVIEW_QUERY = `
  query AiCostOverview($window: CostWindow!, $ownerType: String) {
    aiCostOverview(window: $window, ownerType: $ownerType) {
      totalCostUsd totalCostToman revenueParseh revenueUsd
      blendedCostPerParsehUsd blendedMarginPct invocations calls pricedShare
      daily { ${DAILY_FIELDS} }
      byService { key label costUsd sharePct count }
      byModel { key label costUsd sharePct count }
      alerts { kind severity count detail }
      activePolicyId activePolicyName fxTomanPerUsd parsehToman walletParsehToman
      mostExpensiveService creditCurrency
    }
  }
`;

// ── models ────────────────────────────────────────────────────────────────────

export const MODELS_QUERY = `
  query AiModels($window: CostWindow!, $includeDeprecated: Boolean) {
    aiModels(window: $window, includeDeprecated: $includeDeprecated) {
      modelId provider displayName modalities status notes createdAt updatedAt
      currentPrice { ${PRICE_FIELDS} }
      callsInWindow costUsdInWindow usedByServices
    }
  }
`;

export const MODEL_PRICES_QUERY = `
  query AiModelPrices($modelId: ID!) {
    aiModelPrices(modelId: $modelId) { ${PRICE_FIELDS} }
  }
`;

export const UPSERT_MODEL = `
  mutation UpsertAiModel($input: AiModelInput!) {
    upsertAiModel(input: $input) { modelId status }
  }
`;

export const ADD_MODEL_PRICE = `
  mutation AddAiModelPrice($input: AiModelPriceInput!) {
    addAiModelPrice(input: $input) {
      price { ${PRICE_FIELDS} }
      recomputedCalls recomputedInvocations
    }
  }
`;

// ── services ──────────────────────────────────────────────────────────────────

export const SERVICES_QUERY = `
  query AiServices($window: CostWindow!, $policyId: ID, $includeInactive: Boolean) {
    aiServices(window: $window, policyId: $policyId, includeInactive: $includeInactive) {
      services { ${SERVICE_ROW_FIELDS} }
      policyId fxTomanPerUsd parsehToman creditCurrency
    }
  }
`;

export const SERVICE_DETAIL_QUERY = `
  query AiServiceDetail($serviceKey: ID!, $window: CostWindow!, $policyId: ID) {
    aiServiceDetail(serviceKey: $serviceKey, window: $window, policyId: $policyId) {
      row { ${SERVICE_ROW_FIELDS} }
      models { modelId role calls avgInputTokens avgOutputTokens costUsd shareOfCostPct shareOfCallsPct unpriced }
      steps { step callsPerInvocation avgInputTokens avgOutputTokens costUsd shareOfCostPct }
      declaredRoles { role stepHint expectedCalls observed }
      daily { ${DAILY_FIELDS} }
      pricing {
        basisCostUsd basisSource basisSample fxTomanPerUsd parsehToman
        computedParseh overrideParseh effectiveParseh marginPct policyId policyName
        creditPrice creditCurrency
      }
      billedMinutes audioMinutesSent
    }
  }
`;

// ── ledger ────────────────────────────────────────────────────────────────────

export const INVOCATIONS_QUERY = `
  query AiInvocations($filter: AiInvocationFilter!, $page: Int!, $pageSize: Int!) {
    aiInvocations(filter: $filter, page: $page, pageSize: $pageSize) {
      invocations { ${INVOCATION_FIELDS} }
      total page pageSize
    }
  }
`;

export const INVOCATION_QUERY = `
  query AiInvocation($requestId: ID!) {
    aiInvocation(requestId: $requestId) {
      invocation { ${INVOCATION_FIELDS} }
      calls {
        id step role provider modelId inputTokens outputTokens cachedTokens audioSeconds
        tokensEstimated priceId costUsd providerCostUsd latencyMs status calledAt
      }
    }
  }
`;

/** The two option lists the ledger's filters need, in one round trip. */
export const LEDGER_OPTIONS_QUERY = `
  query AiLedgerOptions($window: CostWindow!) {
    aiServices(window: $window, includeInactive: true) {
      services { serviceKey labelFa labelEn }
    }
    aiModels(window: $window, includeDeprecated: true) { modelId }
  }
`;

// ── policies ──────────────────────────────────────────────────────────────────

export const POLICIES_QUERY = `
  query AiPricingPolicies {
    aiPricingPolicies { ${POLICY_FIELDS} }
  }
`;

export const POLICY_QUERY = `
  query AiPricingPolicy($id: ID!) {
    aiPricingPolicy(id: $id) { ${POLICY_DETAIL_FIELDS} }
  }
`;

export const CREDIT_PRICING_QUERY = `
  query CreditPricing {
    creditPricing { parsehToman creditPrice { amountMinor currency } }
  }
`;

export const EVENTS_QUERY = `
  query AiPricingEvents($page: Int!, $pageSize: Int!) {
    aiPricingEvents(page: $page, pageSize: $pageSize) {
      events { id at actorSub kind entity entityId before after }
      total
    }
  }
`;

export const SAVE_POLICY_DRAFT = `
  mutation SaveAiPricingPolicyDraft($id: ID, $cloneFromId: ID, $input: AiPricingPolicyInput!, $expectedVersion: Int) {
    saveAiPricingPolicyDraft(id: $id, cloneFromId: $cloneFromId, input: $input, expectedVersion: $expectedVersion) {
      ${POLICY_DETAIL_FIELDS}
    }
  }
`;

export const SIMULATE_POLICY = `
  mutation SimulateAiPricingPolicy($id: ID, $input: AiPricingPolicyInput) {
    simulateAiPricingPolicy(id: $id, input: $input) { ${POLICY_DETAIL_FIELDS} }
  }
`;

export const SET_SERVICE_OVERRIDE = `
  mutation SetAiServiceOverride($policyId: ID!, $serviceKey: ID!, $parseh: String, $expectedVersion: Int!) {
    setAiServiceOverride(policyId: $policyId, serviceKey: $serviceKey, parseh: $parseh, expectedVersion: $expectedVersion) {
      ${POLICY_DETAIL_FIELDS}
    }
  }
`;

export const ACTIVATE_POLICY = `
  mutation ActivateAiPricingPolicy($id: ID!, $expectedVersion: Int!) {
    activateAiPricingPolicy(id: $id, expectedVersion: $expectedVersion) { ${POLICY_DETAIL_FIELDS} }
  }
`;

export const ARCHIVE_POLICY = `
  mutation ArchiveAiPricingPolicy($id: ID!) {
    archiveAiPricingPolicy(id: $id) { ${POLICY_DETAIL_FIELDS} }
  }
`;
